import { useTranslation } from "react-i18next";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";
import { formatDateShort } from "../format-user";
import type { UserDetail } from "../types";
import { EMPTY_VALUE, ProfileField } from "./ProfileField";
import { PhoneField } from "./PhoneField";
import { latestTosSummary } from "./latest-tos-summary";

interface UserProfileSectionProps {
  readonly user: UserDetail;
  /** Whether the viewer may verify a phone manually (admin/support). */
  readonly canVerifyPhone: boolean;
  readonly verifyPending: boolean;
  readonly onVerifyPhone: () => void;
}

/** The read-only profile card: identity, contact, dates and the latest ToS acceptance. */
export function UserProfileSection({
  user,
  canVerifyPhone,
  verifyPending,
  onVerifyPhone,
}: UserProfileSectionProps) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="user-detail-profile"
      className="rounded-lg border border-border bg-card p-6"
    >
      <h2 className="brand-eyebrow mb-4">{t("users.detail.profile")}</h2>
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ProfileField label={t("users.detail.email")} value={user.email} />
        <ProfileField
          label={t("users.detail.displayName")}
          value={sanitizeDisplayText(user.displayName) || EMPTY_VALUE}
        />
        <PhoneField
          phone={user.phone}
          verified={user.phoneVerified}
          canVerify={canVerifyPhone}
          isPending={verifyPending}
          onVerify={onVerifyPhone}
        />
        <ProfileField
          label={t("users.detail.companyName")}
          value={user.companyName || EMPTY_VALUE}
        />
        <ProfileField
          label={t("users.detail.created")}
          value={formatDateShort(user.createdAt) || EMPTY_VALUE}
        />
        <ProfileField
          label={t("users.detail.lastSignIn")}
          value={formatDateShort(user.lastSignInAt) || EMPTY_VALUE}
        />
        <ProfileField
          label={t("users.detail.tosAcceptedAt")}
          value={latestTosSummary(user.tosAcceptances) ?? EMPTY_VALUE}
        />
        <ProfileField label={t("users.detail.id")} value={user.id} mono />
      </dl>
    </section>
  );
}
